import { useEffect, useState } from 'react';

import { buildHackySchoolPageUrl } from '../common/buildHackySchoolPageUrl';
import { convertToSchoolMapSearchUrl, makeSearchURL } from '../common/searchRequestUtils';
import { extractSuggestionLocationParameters } from '../common/locationUtils';
import { type GeoLocationPlace } from '../common/location/types';
import { SchoolCardList, type SchoolSuggestionList } from '../common/components/SchoolCardList';
import { SchoolSearch } from '../common/SchoolSearch';
import { type SchoolSuggestion } from '../search/types/typeaheadSuggestions';
import MapCalloutPoint from '../../../images/donor-map/map-callout-point.svg';
import Microscope from '../../../images/school/microscope.svg';
import Pinpoint from '../../../images/donor-map/pinpoint.svg';
import { StateInfo } from './StateInfo';

const SEARCH_URL_MAP = '/donors/search.html?schoolsOnly=true&map=true';
const SEARCH_URL_KEYWORDS = '/donors/search.html?schoolsOnly=true';
const PER_PAGE = 3;
const BASE_MAP_URL = `${baseFrontURL}donors/map.html`;

const NullComponent = () => null;

const getSearchParams = function (location: GeoLocationPlace) {
    const params = extractSuggestionLocationParameters(location);
    params.centerLat = params.centerLat || String(location.latLng?.lat ?? '');
    params.centerLng = params.centerLng || String(location.latLng?.lng ?? '');
    return params;
};

const getMapUrl = function (location?: GeoLocationPlace) {
    if (!location) {
        return BASE_MAP_URL;
    }

    const params = getSearchParams(location);
    return makeSearchURL(params, BASE_MAP_URL);
};

const navigateToSchoolInfoPage = function (selectedSchool: number) {
    window.location.href = buildHackySchoolPageUrl(selectedSchool);
};

const navigateToSearch = function (keywords: string, location: GeoLocationPlace | nullish) {
    // Navigate to search
    const params = location ? getSearchParams(location) : {};
    params.keywords = keywords;
    window.location.href = makeSearchURL(params, SEARCH_URL_KEYWORDS);
};

export const SchoolLevelGivingLanding = function () {
    const [location, setLocation] = useState<GeoLocationPlace | undefined>();
    const [schools, setSchools] = useState<SchoolSuggestionList>([]);

    useEffect(() => {
        if (location) {
            getSchoolSuggestions(location);
        } else {
            setSchools([]);
        }
    }, [location]);

    const getSchoolSuggestions = function (location: GeoLocationPlace) {
        const params = getSearchParams(location);
        const schoolSearchUrl = convertToSchoolMapSearchUrl(makeSearchURL(params, SEARCH_URL_MAP), PER_PAGE);
        setSchools('LOADING');
        $.ajax({
            type: 'GET',
            url: schoolSearchUrl,
            cache: true,
            dataType: 'json',
        }).done((schoolResults: SchoolSuggestion[]) => {
            setSchools(schoolResults);
        }).fail(() => {
            setSchools([]);
        });
    };

    const onSearchButtonClick = function (keywords: string) {
        navigateToSearch(keywords, location);
    };

    const renderSchoolSearch = function () {
        return (
            <form className="form-redux">
                <SchoolSearch
                    location={location}
                    setLocation={setLocation}
                    setSelectedSchool={navigateToSchoolInfoPage}
                    clearSelectedSchool={() => {}}
                    skipAutofocus={true}
                    blockNewRequestForm={true}
                    customSchoolSearchDetailsComponent={NullComponent}
                    showSearchButton={true}
                    onSearchButtonClick={onSearchButtonClick}
                    placeholder="Search schools by name"
                    label="Find a school to support"
                    useMaxMind={true}
                    showLocationNoteOnLength={true}
                />
            </form>);
    };

    const renderSchoolSuggestions = function () {
        if (schools !== 'LOADING' && schools.length === 0) {
            return null;
        }
        const locationHeader = location && <h3>
            Schools in <a href={getMapUrl(location)} className="link-discreet schools-near">{location.cityName || location.zip}{location.stateCode && `, ${location.stateCode}`}</a>
        </h3>;
        return <>
            { locationHeader }
            <SchoolCardList schools={schools}/>
        </>;
    };

    return (
        <div className="school-level-giving-landing">
            <header>
                <h1>Support <em>any</em> public school through DonorsChoose</h1>
                <p className="subheader">
                        Right now, hallways are buzzing with potential. With a little extra support, students can reach
                        it.
                </p>
                {renderSchoolSearch()}
            </header>
            {renderSchoolSuggestions()}
            <div>
                <a className="button-secondary map-button" href={getMapUrl(location)}>See all nearby schools</a>
            </div>
            { location && <section><StateInfo stateCode={location.stateCode}/></section> }
            <div className="financials">
                <Microscope/>
                <h2>Transparency &amp; Efficiency</h2>
                <p className="subheader">Through DonorsChoose, your care and your donation goes straight to
                        classrooms. We show you how every dollar is spent, how you make a difference, and the gratitude
                        felt by students and teachers.</p>
                <p>We&#39;ve received the highest possible ratings from:</p>
                <div className="links">
                    <a className="link-discreet" href="https://www.charitywatch.org/charities/donorschooseorg"
                        target="_blank" rel="noreferrer">Charity Watch</a>
                    <a className="link-discreet" href="https://www.charitynavigator.org/ein/134129457"
                        target="_blank" rel="noreferrer">Charity Navigator</a>
                    <a className="link-discreet"
                        href="https://app.candid.org/profile/6933175/donorschoose-13-4129457"
                        target="_blank" rel="noreferrer">Candid</a>
                </div>
                <a href={baseFrontURL + 'about/finance.html'} className="button-secondary">See financials</a>
            </div>
            <section className="map-feature-callout">
                <div className="container">
                    <div className="fluid-container">
                        <div className="col col6">
                            <div className="point-group">
                                <Pinpoint className="pinpoint" width="88" height="161" />
                            </div>
                            <div className="underlying-project-points">
                                <MapCalloutPoint />
                                <MapCalloutPoint />
                                <MapCalloutPoint />
                                <MapCalloutPoint />
                                <MapCalloutPoint />
                                <MapCalloutPoint />
                            </div>
                        </div>
                        <div className="col col6 map-callout-copy">
                            <h2 className="thick">Find a school near you</h2>
                            <p className="subheader">Zoom into your neighborhood and explore public schools nearby</p>
                            <a className="button-primary" target="_blank"
                                href={getMapUrl(location)} rel="noreferrer">See local area</a>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};
